import axios from "axios";

/**
 * This app is separate from the StayMaster hotel-booking application.
 * Room Service reads room / check-in data live from StayMaster's own API,
 * which rejects every request without a Bearer token ("Access token is
 * required" — confirmed by calling it directly).
 *
 * Primary auth path: StayMaster's sidebar hands off its logged-in user's
 * own access token via a URL fragment when opening this app (see
 * staymasterHandoff.js), which gets stored under TOKEN_KEY. This means
 * Room Service works for ANY client's StayMaster account automatically —
 * no shared credentials to configure or know in advance.
 *
 * Fallback: if no handed-off token is present (e.g. this app was opened
 * directly, not via StayMaster's "Restaurant POS" link), and a service
 * account is configured via REACT_APP_STAYMASTER_EMAIL /
 * REACT_APP_STAYMASTER_PASSWORD in .env, log in with that instead. This
 * is optional — leave those blank if you don't have/want a shared account.
 *
 * Bookings only carry room IDs (comma-separated `rooms` field), not room
 * number/floor/type directly, so those get resolved per-room via
 * GET /rooms/:id (works without a property id) plus GET /room-type for
 * type names.
 */

const STAYMASTER_BASE =
  // process.env.REACT_APP_STAYMASTER_API || "http://localhost:9028/api";
  process.env.REACT_APP_STAYMASTER_API || "https://admin.staymaster.co.in/api";

const SERVICE_EMAIL = process.env.REACT_APP_STAYMASTER_EMAIL;
const SERVICE_PASSWORD = process.env.REACT_APP_STAYMASTER_PASSWORD;

const LOGIN_PATH = "/auth/login";
const ROOM_BOOKINGS_PATH = "/general/get/room_bookings";
const ROOM_TYPES_PATH = "/room-type";
const TOKEN_KEY = "staymaster_token";

const staymasterClient = axios.create({
  baseURL: STAYMASTER_BASE,
  timeout: 15000,
});

let loginPromise = null;

const loginWithServiceAccount = async () => {
  if (!SERVICE_EMAIL || !SERVICE_PASSWORD) {
    throw new Error(
      ""
    );
  }

  const response = await staymasterClient.post(LOGIN_PATH, {
    email: SERVICE_EMAIL,
    password: SERVICE_PASSWORD,
  });

  const data = response.data;

  if (!data?.status || !data?.accessToken) {
    throw new Error(data?.message || "Invalid StayMaster service credentials");
  }

  localStorage.setItem(TOKEN_KEY, data.accessToken);
  return data.accessToken;
};

// De-dupe concurrent logins if multiple requests fire at once with no token yet.
const ensureToken = async () => {
  const existing = localStorage.getItem(TOKEN_KEY);
  if (existing) return existing;

  if (!loginPromise) {
    loginPromise = loginWithServiceAccount().finally(() => {
      loginPromise = null;
    });
  }

  return loginPromise;
};

// Shared GET helper: attaches the token and retries once after a fresh
// login if the current token turns out to be expired/invalid.
const authorizedGet = async (path) => {
  let token = await ensureToken();

  const request = () =>
    staymasterClient.get(path, {
      headers: { Authorization: `Bearer ${token}` },
    });

  try {
    const response = await request();
    return response.data;
  } catch (error) {
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
      localStorage.removeItem(TOKEN_KEY);
      token = await ensureToken();

      const retryResponse = await request();
      return retryResponse.data;
    }

    throw error;
  }
};

const RoomServiceApi = {
  getRoomBookings: () => authorizedGet(ROOM_BOOKINGS_PATH),

  getRoomTypes: () => authorizedGet(ROOM_TYPES_PATH),

  getRoomById: (id) => authorizedGet(`/rooms/${id}`),
};

export default RoomServiceApi;
